// Flow levels ("paliers") of a section: each row ties a flow to a cotation and, optionally, to a gauge mark
// read on the river ("échelle": -1, 0, +2...). Rows marked as recommended define the recommended range.
//
// Stored on the section as `flow_levels: [{ flow, cotation, recommended, gauge_mark }]`. The older single-value
// fields (min/max/recommended_sug_level, class_level_min/class_level/class_level_max) are still read by the
// public tables, SEO and the gauge Cloud Function, so they are derived from the rows on save.

import { toNumberOrNull } from "@/utils/numbers";

export function createFlowLevel(values = {}) {
  return { flow: null, cotation: "", recommended: true, gauge_mark: "", ...values };
}

// Rows for a section saved before flow levels existed: one per filled min / recommended / max pair.
export function legacyFlowLevels(section = {}) {
  return [
    [section.min_sug_level, section.class_level_min],
    [section.recommended_sug_level, section.class_level],
    [section.max_sug_level, section.class_level_max],
  ]
    .map(([flow, cotation]) => createFlowLevel({ flow: toNumberOrNull(flow), cotation: cotation || "" }))
    .filter((level) => level.flow !== null || level.cotation);
}

export function getSectionFlowLevels(section = {}) {
  if (Array.isArray(section.flow_levels) && section.flow_levels.length) {
    return section.flow_levels.map((level) => createFlowLevel(level));
  }
  return legacyFlowLevels(section);
}

// Rows that have a flow, from the lowest to the highest flow.
export function sortFlowLevels(levels = []) {
  return levels
    .map((level) => ({ ...level, flow: toNumberOrNull(level.flow) }))
    .filter((level) => level.flow !== null)
    .sort((a, b) => a.flow - b.flow);
}

// Rows as the admin editor lists them: from the highest to the lowest flow, like the level chart.
// Rows without a flow yet stay at the bottom.
export function sortFlowLevelsHighestFirst(levels = []) {
  const flowOf = (level) => toNumberOrNull(level.flow) ?? -Infinity;
  return [...levels].sort((a, b) => (flowOf(a) === flowOf(b) ? 0 : flowOf(b) - flowOf(a)));
}

// The lowest and highest recommended flows bound the recommended range; its middle is the ideal flow.
export function deriveSectionLevels(levels = []) {
  const sorted = sortFlowLevels(levels);
  const recommended = sorted.filter((level) => level.recommended);
  const min = recommended.length ? recommended[0].flow : null;
  const max = recommended.length ? recommended[recommended.length - 1].flow : null;
  const ideal = recommended.length ? Math.round(((min + max) / 2) * 100) / 100 : null;

  // Cotation of the recommended row closest to the ideal flow (the lower one on a tie)
  const idealLevel = recommended.reduce(
    (best, level) => (!best || Math.abs(level.flow - ideal) < Math.abs(best.flow - ideal) ? level : best),
    null,
  );

  return {
    min_sug_level: min,
    max_sug_level: max,
    recommended_sug_level: ideal,
    class_level_min: sorted.length ? sorted[0].cotation || "" : "",
    class_level: idealLevel ? idealLevel.cotation || "" : "",
    class_level_max: sorted.length ? sorted[sorted.length - 1].cotation || "" : "",
  };
}

// "min" below the recommended range, "recommended" inside it, "max" above it.
export function getFlowLevelZone(level, derived) {
  const flow = toNumberOrNull(level.flow);
  if (flow === null || derived.min_sug_level === null) {
    return null;
  }
  if (flow < derived.min_sug_level) return "min";
  if (flow > derived.max_sug_level) return "max";
  return "recommended";
}

// Cotation for a flow: the one of the highest row at or below it (the lowest row below all of them).
export function getCotationForFlow(levels, flow) {
  const sorted = sortFlowLevels(levels);
  if (!sorted.length || !Number.isFinite(flow)) {
    return "";
  }
  const below = sorted.filter((level) => level.flow <= flow);
  return (below.length ? below[below.length - 1] : sorted[0]).cotation || "";
}
